import KickerBadge from "@/components/course/KickerBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  formatQuizDateTime,
  formatQuizTime,
  formatRemainingTime,
  percentOf,
} from "@/lib/courseCompletionQuizFormat";
import type {
  CompletionQuizResult,
  CourseCompletionQuizSummary,
} from "@/types/courseCompletionQuiz";

interface CourseCompletionQuizCardProps {
  summary: CourseCompletionQuizSummary;
  courseTitle: string;
  /** Share of the course the learner has worked through, 0-100. */
  progressPercent: number;
  /** The last finished run, for the states that look back at it. */
  lastResult?: CompletionQuizResult | null;
  /** Opens whatever the state leads to: intro, open run or result. */
  onAction: () => void;
  now?: Date;
}

interface CardContent {
  title: string;
  /** Sits on the same line as the title. The attempt count, while a run is open or waiting. */
  titleAside?: string;
  text?: string;
  actionLabel?: string;
  hint?: string;
}

const attemptCountLabel = (summary: CourseCompletionQuizSummary) =>
  `Versuch ${summary.attemptsUsed} von ${summary.maxAttempts}`;

const failedBadge = {
  label: "Nicht bestanden",
  className: "border-transparent bg-error-light text-error-foreground",
};

const STATUS_BADGE: Partial<
  Record<
    CourseCompletionQuizSummary["status"],
    { label: string; className: string }
  >
> = {
  in_progress: {
    label: "in Bearbeitung",
    className: "border-transparent bg-info-light text-info-foreground",
  },
  passed: {
    label: "Bestanden",
    className: "border-transparent bg-success-light text-success-foreground",
  },
  waiting: failedBadge,
  failed_final: failedBadge,
};

const describeCard = ({
  summary,
  courseTitle,
  progressPercent,
  lastResult,
  now = new Date(),
}: Omit<CourseCompletionQuizCardProps, "onAction">): CardContent | null => {
  if (summary.status === "locked") return null;

  const progress = Math.round(progressPercent);

  switch (summary.status) {
    case "startable":
      if (summary.attemptsUsed > 0) {
        return {
          title: "Du kannst das Quiz erneut versuchen",
          text: "Die Wartezeit ist vorbei. Starte einen neuen Versuch, wenn du bereit bist.",
          actionLabel: "Neuen Versuch starten",
        };
      }
      return {
        title: "Dein Kursabschlussquiz ist bereit",
        text: `Du hast ${progress} % des Kurses bearbeitet. Beantworte ${summary.questionCount} Fragen und schließe den Kurs mit mindestens ${summary.passPercent} % ab.`,
        actionLabel: "Zum Quiz",
      };
    case "in_progress":
      return {
        title: "Kursabschlussquiz",
        titleAside: attemptCountLabel(summary),
        actionLabel: "Fortsetzen",
      };
    case "waiting": {
      const remaining = summary.nextAttemptAt
        ? formatRemainingTime(summary.nextAttemptAt, now)
        : null;
      const last = lastResult
        ? `Letztes Ergebnis: ${percentOf(lastResult.correctCount, lastResult.totalCount)} % (${lastResult.correctCount} von ${lastResult.totalCount}). `
        : "";
      const from = summary.nextAttemptAt
        ? ` – ab ${formatQuizDateTime(summary.nextAttemptAt)} Uhr kannst du neu starten`
        : "";
      return {
        title: remaining
          ? `Nächster Versuch in ${remaining}`
          : "Nächster Versuch gleich möglich",
        titleAside: attemptCountLabel(summary),
        text: `${last}Nutze die Zeit zum Wiederholen${from}.`,
        actionLabel: "Ergebnis ansehen",
        hint: summary.nextAttemptAt
          ? `Ab ${formatQuizTime(summary.nextAttemptAt)} Uhr`
          : undefined,
      };
    }
    case "failed_final":
      return {
        title: `Du hast alle ${summary.maxAttempts} Versuche genutzt`,
        text: "Ein weiterer Versuch ist nicht möglich. Wende dich an deine Kursbegleitung, um zu klären, wie du den Kurs trotzdem abschließen kannst.",
        actionLabel: "Ergebnis ansehen",
      };
    case "passed":
      return {
        title: lastResult
          ? `Kurs bestanden mit ${percentOf(lastResult.correctCount, lastResult.totalCount)} %`
          : "Kurs bestanden",
        text: `Glückwunsch! Du hast „${courseTitle}“ ${lastResult ? `im ${lastResult.attemptNumber}. Versuch ` : ""}erfolgreich abgeschlossen.`,
        actionLabel: "Ergebnis ansehen",
      };
  }
};

const CardAction = ({
  content,
  onAction,
}: {
  content: CardContent;
  onAction: () => void;
}) => {
  if (content.actionLabel) {
    return (
      <Button
        type="button"
        size="lg"
        className="mt-3 self-start"
        onClick={onAction}
      >
        {content.actionLabel}
      </Button>
    );
  }
  if (!content.hint) return null;
  return (
    <span className="mt-3 self-start rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-muted-foreground">
      {content.hint}
    </span>
  );
};

const StatusBadge = ({
  status,
}: {
  status: CourseCompletionQuizSummary["status"];
}) => {
  const badge = STATUS_BADGE[status];
  if (!badge) return null;
  return (
    <Badge
      variant="outline"
      className={cn("whitespace-normal", badge.className)}
    >
      {badge.label}
    </Badge>
  );
};

/** The lime “new” mark, only before the first attempt. Starting the quiz removes it. */
const renderStatusMark = (summary: CourseCompletionQuizSummary) => {
  if (summary.status === "startable" && summary.attemptsUsed === 0) {
    return (
      <KickerBadge className="whitespace-normal normal-case">
        Neu freigeschaltet
      </KickerBadge>
    );
  }
  return <StatusBadge status={summary.status} />;
};

/**
 * Dashboard entry for the course completion quiz, once it is ready. A locked
 * quiz renders nothing. What the card says and where its button leads depends
 * on where the learner stands (see the status).
 */
export const CourseCompletionQuizCard = ({
  onAction,
  ...props
}: CourseCompletionQuizCardProps) => {
  const { summary } = props;
  const content = describeCard(props);
  if (!content) return null;

  return (
    <Card data-status={summary.status} className="mt-4 gap-0 px-4 py-5">
      <div className="min-w-0 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3
              className="text-heading-h4"
              style={
                { "--heading-color": "var(--primary)" } as React.CSSProperties
              }
            >
              {content.title}
            </h3>
            {content.titleAside && (
              <p className="text-sm text-foreground">{content.titleAside}</p>
            )}
          </div>
          {renderStatusMark(summary)}
        </div>
        {content.text && (
          <p className="mt-1 text-sm text-foreground">{content.text}</p>
        )}
      </div>

      <CardAction content={content} onAction={onAction} />
    </Card>
  );
};
